import * as THREE from 'three';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { LabelMesh } from './LabelMesh';
import { POI } from '../poi/POI';
import { Scene } from '../core/Scene';
import { LabelMeshType } from '../types';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';

/**
 * 🌐 ScreenHTMLLabelMesh - 2D Screen-Space HTML Labels
 * 
 * Unlike HTMLLabelMesh (which uses CSS3D in 3D space), this creates:
 * - Pure HTML elements positioned in 2D screen coordinates  
 * - Fixed screen position relative to POI's projected position
 * - Fade in/out animations based on POI activation state
 * - No 3D rotation or perspective distortion
 * 
 * Perfect for UI-style overlays that should always face the user.
 */
export class ScreenHTMLLabelMesh extends LabelMesh {
    private _element: HTMLElement | null = null;
    private _container: HTMLElement | null = null;
    private _fadeAnimation: Animation | null = null;
    private _isVisible: boolean = false;
    private _lastScreenPosition: { x: number; y: number } | null = null;
    
    // 🎯 Interpolated screen position properties for smooth movement
    private _screenX: InterpolatedProperty<number>;
    private _screenY: InterpolatedProperty<number>;
    
    // 🎯 Fixed width to prevent layout shifts during movement
    private _calculatedWidth: number | null = null;
    
    // 🎯 Deadzone to prevent micro-jitter from small camera movements
    private static readonly MOVEMENT_THRESHOLD = 10.0; // Only interpolate if movement > 3px

    // Global container for all screen-space labels
    private static _globalContainer: HTMLElement | null = null;

    constructor(poi: POI) {
        super(poi, 'ScreenHTMLLabelMesh');
        
        // Initialize interpolated screen position properties
        this._screenX = new InterpolatedProperty<number>(
            `${poi.name}_ScreenX`, 
            0, // initial value
            0, // default value
            1, // duration for smooth transitions (150ms)
            'Screen Position'
        );
        
        this._screenY = new InterpolatedProperty<number>(
            `${poi.name}_ScreenY`, 
            0, // initial value
            0, // default value
            1, // duration for smooth transitions (150ms)
            'Screen Position'
        );
        
        this.ensureGlobalContainer();
    }

    /**
     * Ensure global container exists for all screen HTML labels
     */
    private ensureGlobalContainer(): void {
        if (!ScreenHTMLLabelMesh._globalContainer) {
            ScreenHTMLLabelMesh._globalContainer = document.createElement('div');
            ScreenHTMLLabelMesh._globalContainer.id = 'screen-html-labels-container';
            ScreenHTMLLabelMesh._globalContainer.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                pointer-events: none;
                z-index: 200;
                overflow: hidden;
            `;
            document.body.appendChild(ScreenHTMLLabelMesh._globalContainer);
            console.log('🌐 Created global screen HTML labels container');
        }
    }

    /**
     * Initialize the screen-space HTML element
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) return;

        try {
            // Create the HTML element
            this._element = document.createElement('div');
            this._element.className = 'screen-html-label';
            this._element.style.cssText = `
                position: absolute;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 12px 16px;
                border-radius: 8px;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                font-size: 14px;
                line-height: 1.4;
                max-width: 300px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                backdrop-filter: blur(8px);
                opacity: 0;
                transform: translateZ(0);
                transition: opacity 0.3s ease-in-out;
                pointer-events: auto; /* Will be overridden by updatePointerEvents() */
                word-wrap: break-word;
                z-index: 201;
            `;

            // Add to global container
            if (ScreenHTMLLabelMesh._globalContainer) {
                ScreenHTMLLabelMesh._globalContainer.appendChild(this._element);
            }
            
            // 🎯 Configure pointer events based on labelInteractive setting
            this.updatePointerEvents();

            // Set initial content
            const content = this.poi.poidata.html?.value || this.poi.name || 'Screen HTML Label';
            this.updateContent(content);

            // Apply style
            this.updateStyle();
            
            // 🎯 Listen to labelInteractive property changes
            this.poi.poidata.labelInteractive.on(() => {
                this.updatePointerEvents();
            });
            
            // 🎯 Calculate and fix optimal width to prevent layout shifts
            this.calculateOptimalWidth();

            // Create invisible 3D mesh (for bounds/raycasting compatibility)
            this.createInvisible3DMesh();

            // Register with Scene for automatic updates
            if (Scene.Instance) {
                Scene.Instance.registerScreenLabel(this);
            }

            this._isInitialized = true;
            //console.log(`✅ ScreenHTMLLabelMesh initialized for ${this.poi.name}`);

        } catch (error) {
            console.error(`❌ Error initializing ScreenHTMLLabelMesh for ${this.poi.name}:`, error);
        }
    }

    /**
     * Create an invisible 3D mesh for compatibility with the base system
     */
    private createInvisible3DMesh(): void {
        // Create minimal invisible geometry for bounds/raycasting
        this._geometry = new THREE.PlaneGeometry(0.01, 0.01);
        
        // Create invisible material
        const material = new THREE.MeshBasicMaterial({ 
            transparent: true, 
            opacity: 0,
            visible: false 
        });
        
        this._mesh = new THREE.Mesh(this._geometry, material);
        this._mesh.name = `ScreenHTMLLabel_${this.poi.name}`;
        
        // Add to 3D scene (invisible, for system compatibility)
        if (Scene.Instance) {
            Scene.Instance.add(this._mesh);
        }
    }

    /**
     * 🎯 Calculate optimal width based on content and fix it to prevent layout shifts
     */
    private calculateOptimalWidth(): void {
        if (!this._element) return;

        // If developer provided a max width via POI/App settings, honor it and skip auto calculation
        const customMax = this.poi.poidata.labelMaxWidth?.value ?? 0;
        if (typeof customMax === 'number' && customMax > 0) {
            this._calculatedWidth = customMax;
            this._element.style.width = `${customMax}px`;
            this._element.style.maxWidth = `${customMax}px`;
            this._element.style.minWidth = `${customMax}px`;
            return;
        }

        // Temporarily remove max-width to measure natural width
        const originalMaxWidth = this._element.style.maxWidth;
        this._element.style.maxWidth = 'none';
        this._element.style.width = 'auto';
        
        // Position off-screen to avoid visual flicker
        const originalLeft = this._element.style.left;
        const originalTop = this._element.style.top;
        this._element.style.left = '-9999px';
        this._element.style.top = '-9999px';
        this._element.style.opacity = '1'; // Ensure it's visible for measurement
        
        // Force a layout to get accurate measurements
        this._element.offsetHeight; // Trigger reflow
        
        // Measure the natural width
        const naturalWidth = this._element.offsetWidth;
        
        // Calculate optimal width (but cap it at reasonable limits)
        const maxAllowedWidth = Math.min(window.innerWidth * 0.4, 400); // 40% of screen or 400px max
        const minAllowedWidth = 150; // Minimum readable width
        
        this._calculatedWidth = Math.max(
            minAllowedWidth,
            Math.min(naturalWidth, maxAllowedWidth)
        );
        
        // Apply the calculated fixed width
        this._element.style.width = `${this._calculatedWidth}px`;
        this._element.style.maxWidth = `${this._calculatedWidth}px`;
        this._element.style.minWidth = `${this._calculatedWidth}px`;
        
        // Restore original position and opacity
        this._element.style.left = originalLeft;
        this._element.style.top = originalTop;
        this._element.style.opacity = '0'; // Back to initial hidden state
        
        //console.log(`🎯 Calculated fixed width for ${this.poi.name}: ${this._calculatedWidth}px (natural: ${naturalWidth}px)`);
    }

    /**
     * Update content with Markdown support
     */
    updateContent(text: string): void {
        if (!this._element) return;

        try {
            // Parse Markdown to HTML
            const htmlContent = marked.parse(text) as string;
            
            // Sanitize HTML
            const cleanHtml = DOMPurify.sanitize(htmlContent, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre'],
                ALLOWED_ATTR: []
            });

            this._element.innerHTML = cleanHtml;
            
            // 🎯 Recalculate width after content change to prevent layout shifts
            this.calculateOptimalWidth();
            
        } catch (error) {
            console.error('Error updating ScreenHTML content:', error);
            this._element.textContent = text; // Fallback to plain text
            
            // 🎯 Recalculate width even for fallback text
            this.calculateOptimalWidth();
        }
    }

    /**
     * 🎯 Configure pointer events based on labelInteractive setting  
     * This controls whether labels block camera controls or allow click-through
     */
    private updatePointerEvents(): void {
        if (!this._element) return;
        
        const isInteractive = this.poi.poidata.labelInteractive.value ?? false;
        
        if (isInteractive) {
            // ✅ Label is interactive - allows clicks, hovers, etc.
            this._element.style.pointerEvents = 'auto';
            this._element.style.cursor = 'pointer';
            //console.log(`🖱️ ScreenHTMLLabel ${this.poi.name}: INTERACTIVE mode - label can receive mouse events`);
        } else {
            // 🚫 Label is non-interactive - click-through to camera controls
            this._element.style.pointerEvents = 'none';
            this._element.style.cursor = 'default';
            //console.log(`👻 ScreenHTMLLabel ${this.poi.name}: NON-INTERACTIVE mode - mouse events pass through to camera`);
        }
    }

    /**
     * Update label style
     */
    updateStyle(): void {
        if (!this._element) return;

        // Apply POI-specific styling
        const backgroundColor = this.poi.poidata.labelBackgroundColor?.value;
        const backgroundGradient = this.poi.poidata.labelBackgroundGradient.value;
        const textColor = this.poi.poidata.labelColor?.value;
        const fontSize = this.poi.poidata.labelFontSize?.value;

        // Apply background: gradient takes priority over solid color
        if (backgroundGradient && backgroundGradient.trim()) {
            this._element.style.background = backgroundGradient;
            this._element.style.backgroundColor = ''; // Clear solid color
        } else if (backgroundColor) {
            this._element.style.backgroundColor = backgroundColor.toString();
            this._element.style.background = ''; // Clear gradient
        }

        if (textColor) {
            this._element.style.color = textColor.toString();
        }

        if (fontSize) {
            this._element.style.fontSize = `${fontSize}px`;
        }

        // Apply background mode
        const backgroundMode = this.poi.poidata.labelBackgroundMode?.value;
        if (backgroundMode === null) {
            this._element.style.backgroundColor = 'transparent';
            this._element.style.backdropFilter = 'none';
            this._element.style.boxShadow = 'none';
        }
        
        // 🎯 Update pointer events when style changes
        this.updatePointerEvents();
    }

    /**
     * 🎭 Show label with fade-in animation
     */
    public show(): void {
        if (!this._element || this._isVisible) return;

        // Cancel any existing animation
        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
        }

        this._isVisible = true;
        
        // Start fade-in animation (pure opacity)
        this._fadeAnimation = this._element.animate([
            { opacity: 0 },
            { opacity: 1 }
        ], {
            duration: 300,
            easing: 'ease-out',
            fill: 'forwards'
        });

        //console.log(`🎭 ScreenHTML label shown: ${this.poi.name}`);
    }

    /**
     * 🎭 Hide label with fade-out animation
     */
    public hide(): void {
        if (!this._element || !this._isVisible) return;

        // Cancel any existing animation
        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
        }

        this._isVisible = false;

        // Start fade-out animation (pure opacity)
        this._fadeAnimation = this._element.animate([
            { opacity: 1 },
            { opacity: 0 }
        ], {
            duration: 250,
            easing: 'ease-in',
            fill: 'forwards'
        });

        //console.log(`🎭 ScreenHTML label hidden: ${this.poi.name}`);
    }

    /**
     * Update screen position based on 3D world position
     */
    public updateScreenPosition(camera: THREE.Camera, renderer: THREE.WebGLRenderer): void {
        if (!this._element || !this._mesh) return;

        // Project 3D position to screen coordinates
        const worldPosition = new THREE.Vector3();
        this._mesh.getWorldPosition(worldPosition);

        // Project to screen space
        const screenPosition = worldPosition.clone();
        screenPosition.project(camera);

        // Convert to pixel coordinates
        const canvas = renderer.domElement;
        const x = (screenPosition.x * 0.5 + 0.5) * canvas.clientWidth;
        const y = (screenPosition.y * -0.5 + 0.5) * canvas.clientHeight;

        // 🎯 Check if position changed significantly (3px threshold to prevent micro-jitter)
        if (this._lastScreenPosition) {
            const dx = Math.abs(x - this._lastScreenPosition.x);
            const dy = Math.abs(y - this._lastScreenPosition.y);
            if (dx < ScreenHTMLLabelMesh.MOVEMENT_THRESHOLD && dy < ScreenHTMLLabelMesh.MOVEMENT_THRESHOLD) {
                return; // Skip if movement is less than 3px in both directions
            }
        }

        // 🎯 Update interpolated properties target values for smooth movement
        this._screenX.targetValue = x;
        this._screenY.targetValue = y;

        this._lastScreenPosition = { x, y };
    }

    /**
     * 🎯 Apply interpolated position values to the HTML element styling
     */
    public updateInterpolatedPosition(): void {
        if (!this._element) return;

        // Get current interpolated values
        const currentX = this._screenX.value || 0;
        const currentY = this._screenY.value || 0;

        // 🎯 Round to whole pixels to prevent browser subpixel rendering issues
        const roundedX = Math.round(currentX);
        const roundedY = Math.round(currentY);

        // Apply to CSS positioning with integer pixel values
        this._element.style.left = `${roundedX}px`;
        this._element.style.top = `${roundedY}px`;
        
        // Center the element on the position
        this._element.style.transform = 'translate(-50%, -50%)';
    }

    /**
     * Check if POI is in view and should show label
     */
    public updateVisibilityBasedOnDistance(camera: THREE.Camera): void {
        if (!this._mesh) return;

        // 🎯 Check if fadeLabelOnActive is enabled - if so, don't override POI visibility logic
        const fadeLabelOnActive = this.poi.poidata.fadeLabelOnActive.value;
        
        if (fadeLabelOnActive) {
            // POI controls visibility completely - distance has no effect
            return;
        }

        const worldPosition = new THREE.Vector3();
        this._mesh.getWorldPosition(worldPosition);

        const distance = camera.position.distanceTo(worldPosition);
        
        const isClose = distance < 10; // Adjust threshold as needed

        // Only apply distance-based visibility when fadeLabelOnActive is disabled
        if (isClose && !this._isVisible) {
            this.show();
        } else if (!isClose && this._isVisible) {
            this.hide();
        }
    }

    /**
     * Dispose of the screen HTML label
     */
    dispose(): void {
        // Cancel any running animation
        if (this._fadeAnimation) {
            this._fadeAnimation.cancel();
            this._fadeAnimation = null;
        }

        // Unregister from Scene
        if (Scene.Instance) {
            Scene.Instance.unregisterScreenLabel(this);
        }

        // Remove HTML element
        if (this._element && this._element.parentNode) {
            this._element.parentNode.removeChild(this._element);
        }
        this._element = null;

        // Remove from 3D scene
        if (this._mesh && Scene.Instance) {
            Scene.Instance.remove(this._mesh);
        }

        // Dispose geometry and mesh
        if (this._geometry) {
            this._geometry.dispose();
            this._geometry = null;
        }
        this._mesh = null;

        // 🎯 Clean up interpolated properties (no explicit dispose needed)

        this._isInitialized = false;
        this._isVisible = false;
        this._lastScreenPosition = null;

        //console.log(`🗑️ ScreenHTMLLabelMesh disposed for ${this.poi.name}`);
    }

    /**
     * Override getAspectRatio for screen labels
     */
    public getAspectRatio(): number {
        // Screen labels don't need aspect ratio adjustments
        return 1.0;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    getRenderType(): LabelMeshType {
        return LabelMeshType.SCREEN_HTML;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected getCanvasWidth(): number {
        return this._element ? this._element.offsetWidth || 300 : 300;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected getCanvasHeight(): number {
        return this._element ? this._element.offsetHeight || 200 : 200;
    }

    /**
     * Implementation of abstract method from LabelMesh
     */
    protected applyOpacityToMaterial(opacity: number): void {
        // For screen HTML labels, apply opacity to the HTML element
        if (this._element) {
            this._element.style.opacity = opacity.toString();
        }
    }

    /**
     * Static method to update all screen labels
     */
    public static updateAllScreenLabels(camera: THREE.Camera, renderer: THREE.WebGLRenderer): void {
        // This will be called from the render loop
        // Find all ScreenHTMLLabelMesh instances and update them
    }



    /**
     * Static cleanup method
     */
    public static dispose(): void {
        if (ScreenHTMLLabelMesh._globalContainer && ScreenHTMLLabelMesh._globalContainer.parentNode) {
            ScreenHTMLLabelMesh._globalContainer.parentNode.removeChild(ScreenHTMLLabelMesh._globalContainer);
            ScreenHTMLLabelMesh._globalContainer = null;
        }
    }
}